import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Table, Spinner, Input, Badge, Progress, Alert } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetStocktakeByIdQuery, useUpdateStocktakeMutation, useCompleteStocktakeMutation, useVerifyStocktakeMutation } from '../../../../store/services/inventoryApi';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, CheckCircle, Search, AlertTriangle } from 'react-feather';

const StocktakeSession = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: stocktakeResponse, isLoading, refetch } = useGetStocktakeByIdQuery(id);
    const [updateStocktake, { isLoading: isUpdating }] = useUpdateStocktakeMutation();
    const [completeStocktake, { isLoading: isCompleting }] = useCompleteStocktakeMutation();
    const [verifyStocktake, { isLoading: isVerifying }] = useVerifyStocktakeMutation();

    const [counts, setCounts] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [systemQtyOverrides, setSystemQtyOverrides] = useState({});
    const [conflictVariantIds, setConflictVariantIds] = useState(new Set());

    const stocktake = stocktakeResponse?.data || stocktakeResponse;

    useEffect(() => {
        if (stocktake?.items) {
            const initialCounts = {};
            stocktake.items.forEach(item => {
                const vId = item.productVariantId._id || item.productVariantId;
                initialCounts[vId] = (item.countedQty !== undefined && item.countedQty !== null)
                    ? item.countedQty
                    : (item.systemQty || 0);
            });
            setCounts(initialCounts);
            setSystemQtyOverrides({});
            setConflictVariantIds(new Set());
        }
    }, [stocktake]);

    const handleCountChange = (variantId, val) => {
        setCounts(prev => ({ ...prev, [variantId]: parseInt(val) || 0 }));
    };

    const handleSaveProgress = async () => {
        try {
            const items = Object.keys(counts).map(variantId => ({
                productVariantId: variantId,
                countedQty: counts[variantId]
            }));
            await updateStocktake({ id, items }).unwrap();
            toast.success('Progress saved successfully');
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || 'Failed to save progress');
        }
    };

    const handleComplete = async () => {
        if (!window.confirm('Are you sure you want to COMPLETE this stocktake? This will finalize variances and potentially adjust store stock.')) {
            return;
        }

        try {
            // Step 1 — save current counts
            const items = Object.keys(counts).map(variantId => ({
                productVariantId: variantId,
                countedQty: counts[variantId]
            }));
            await updateStocktake({ id, items }).unwrap();

            // Step 2 — verify: re-read live stock and compare systemQty
            try {
                await verifyStocktake(id).unwrap();
            } catch (verifyErr) {
                // RTK Query surfaces 4xx/5xx errors here
                const payload = verifyErr?.data;
                if (payload && payload.ok === false && Array.isArray(payload.conflicts) && payload.conflicts.length > 0) {
                    const overrides = {};
                    const conflictIds = new Set();
                    payload.conflicts.forEach(c => {
                        overrides[c.productVariantId] = c.freshSystemQty;
                        conflictIds.add(c.productVariantId);
                    });
                    setSystemQtyOverrides(overrides);
                    setConflictVariantIds(conflictIds);
                    toast.warning(
                        `Stock values changed for ${payload.conflicts.length} item(s). Review the updated System Qty values highlighted below, then retry.`,
                        { autoClose: 10000 }
                    );
                    return;
                }
                throw verifyErr;
            }

            // Step 3 — verify succeeded (no 409 thrown) → no conflicts, proceed to complete
            await completeStocktake(id).unwrap();
            toast.success('Stocktake Completed and Reconciled');
            navigate(`/inventory/stocktakes/${id}`);
        } catch (err) {
            toast.error(err?.data?.message || 'Failure during completion');
        }
    };

    if (isLoading) return <div className="text-center p-5"><Spinner color="primary" /></div>;
    if (!stocktake) return <div className="text-center p-5">Stocktake session not found</div>;
    if (stocktake.status === 'Completed') {
        navigate(`/inventory/stocktakes/${id}`);
        return null;
    }

    const filteredItems = stocktake.items?.filter(item => {
        const name = (item.productVariantId?.name || item.productVariantId?.productName || '').toLowerCase();
        const sku = (item.productVariantId?.sku || '').toLowerCase();
        return name.includes(searchTerm.toLowerCase()) || sku.includes(searchTerm.toLowerCase());
    }) || [];

    const totalItems = stocktake.items?.length || 0;
    const countedItems = Object.values(counts).filter(c => c > 0).length;
    const progressPerc = totalItems > 0 ? (countedItems / totalItems) * 100 : 0;
    const isBusy = isUpdating || isVerifying || isCompleting;
    const hasConflicts = conflictVariantIds.size > 0;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Counting Session" parent="Inventory" title={`ST-${stocktake.stocktakeNumber || id.slice(-6)}`} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <div>
                                    <h5>Session: #{stocktake.stocktakeNumber}</h5>
                                    <Badge color="info" className="me-2">{stocktake.type}</Badge>
                                    <Badge color="warning">In Progress</Badge>
                                    <div className="mt-2" style={{ minWidth: '200px' }}>
                                        <small className="text-muted">Progress: {countedItems}/{totalItems} items counted</small>
                                        <Progress value={progressPerc} size="xs" color="success" />
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button color="light" onClick={() => navigate('/inventory/stocktakes')}>
                                        <ArrowLeft size={14} className="me-1" /> Exit
                                    </Button>
                                    <Button color="secondary" onClick={handleSaveProgress} disabled={isBusy}>
                                        {isUpdating ? <Spinner size="sm" /> : <><Save size={14} className="me-1" /> Save Progress</>}
                                    </Button>
                                    <Button color="success" onClick={handleComplete} disabled={isBusy}>
                                        {(isVerifying || isCompleting)
                                            ? <Spinner size="sm" />
                                            : <><CheckCircle size={14} className="me-1" /> Finalize &amp; Reconcile</>
                                        }
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {hasConflicts && (
                                    <Alert color="warning" className="d-flex align-items-start mb-4">
                                        <AlertTriangle size={20} className="me-3 flex-shrink-0 mt-1" />
                                        <div>
                                            <strong>Store stock changed since this session started ({conflictVariantIds.size} item{conflictVariantIds.size > 1 ? 's' : ''} affected).</strong>
                                            <p className="mb-0 mt-1 small">
                                                Highlighted rows show the updated <strong>System Qty</strong> from the datastore (old values shown below in red).
                                                Adjust your <strong>Counted Qty</strong> if needed, then click <strong>Finalize &amp; Reconcile</strong> again.
                                            </p>
                                        </div>
                                    </Alert>
                                )}

                                <div className="mb-4 d-flex align-items-center gap-3">
                                    <div className="flex-grow-1">
                                        <div className="input-group">
                                            <span className="input-group-text"><Search size={16} /></span>
                                            <Input
                                                type="text"
                                                placeholder="Search items in this session..."
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <h6 className="mb-0">{stocktake.storeId?.storeName}</h6>
                                        <small className="text-muted">Store Snapshot Date: {new Date(stocktake.createdAt).toLocaleString()}</small>
                                    </div>
                                </div>

                                <Table responsive bordered hover>
                                    <thead className="table-light text-center">
                                        <tr>
                                            <th>#</th>
                                            <th className="text-start">Product / Variant</th>
                                            <th>SKU</th>
                                            <th>System Qty</th>
                                            <th style={{ width: '150px' }}>Counted Qty</th>
                                            <th>Variance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-center align-middle">
                                        {filteredItems.map((item, index) => {
                                            const vId = item.productVariantId._id || item.productVariantId;
                                            const vIdStr = vId?.toString();
                                            const counted = counts[vId] || 0;
                                            const isConflict = conflictVariantIds.has(vIdStr);
                                            const system = isConflict
                                                ? (systemQtyOverrides[vIdStr] ?? item.systemQty)
                                                : (item.systemQty || 0);
                                            const variance = counted - system;

                                            return (
                                                <tr key={vId} className={isConflict ? 'table-warning' : ''}>
                                                    <td>{index + 1}</td>
                                                    <td className="text-start">
                                                        <div className="fw-bold">{item.productVariantId?.productName}</div>
                                                        <small className="text-muted">{item.productVariantId?.variantName}</small>
                                                    </td>
                                                    <td>{item.productVariantId?.sku}</td>
                                                    <td className={isConflict ? 'fw-bold' : 'bg-light'}>
                                                        <span>{system}</span>
                                                        {isConflict && (
                                                            <div>
                                                                <small className="text-danger" style={{ fontSize: '0.7rem' }}>
                                                                    &#8593; was {item.systemQty}
                                                                </small>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            className="text-center fw-bold"
                                                            value={counted}
                                                            onChange={(e) => handleCountChange(vId, e.target.value)}
                                                        />
                                                    </td>
                                                    <td className={`fw-bold ${variance === 0 ? 'text-success' : variance > 0 ? 'text-info' : 'text-danger'}`}>
                                                        {variance > 0 ? `+${variance}` : variance}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filteredItems.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="p-5 text-muted">No items found matching search criteria.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default StocktakeSession;
